/**
 * Modal de saisie multi-lots pour SADAStock.
 *
 * Permet de répartir la quantité physique d'un produit en plusieurs lots
 * (qty + date péremption + numéro de lot facultatif).
 *
 * Props :
 *  - line: la ligne d'inventaire (avec produit, théorique, lots existants)
 *  - onSave(lots: Array<{qty, expiration_date, lot_number}>)  => Promise
 *  - onClose()
 */
import React, { useState } from 'react';
import { X, Plus, Trash2, Layers } from 'lucide-react';
import { Button } from '../../ui/button';

const blankLot = () => ({ qty: '', expiration_date: '', lot_number: '' });

const LotsDialog = ({ line, onSave, onClose }) => {
  const initial = Array.isArray(line?.lots) && line.lots.length
    ? line.lots.map(l => ({
        qty: String(l.qty ?? ''),
        expiration_date: l.expiration_date || '',
        lot_number: l.lot_number || '',
      }))
    : [blankLot(), blankLot()];
  const [lots, setLots] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const updateLot = (idx, field, val) => {
    setLots(prev => prev.map((l, i) => (i === idx ? { ...l, [field]: val } : l)));
  };

  const addLot = () => setLots(prev => [...prev, blankLot()]);
  const removeLot = (idx) => setLots(prev => prev.filter((_, i) => i !== idx));

  const total = lots.reduce((acc, l) => acc + (parseInt(l.qty, 10) || 0), 0);
  const stockTh = parseInt(line?.stock_theorique, 10) || 0;
  const ecart = total - stockTh;

  const submit = async () => {
    setError(null);
    // Validation : ignore les lignes complètement vides ; rejette qty<0
    const payload = [];
    for (const l of lots) {
      const qty = parseInt(l.qty, 10);
      if (!l.qty && !l.expiration_date && !l.lot_number) continue; // ligne vide
      if (Number.isNaN(qty) || qty < 0) {
        setError('Chaque lot doit avoir une quantité ≥ 0');
        return;
      }
      const lotEntry = { qty };
      if (l.expiration_date) lotEntry.expiration_date = l.expiration_date;
      if (l.lot_number?.trim()) lotEntry.lot_number = l.lot_number.trim();
      payload.push(lotEntry);
    }
    if (!payload.length) {
      setError('Ajoutez au moins un lot');
      return;
    }
    setSaving(true);
    try {
      await onSave(payload);
      onClose();
    } catch (e) {
      setError(e?.message || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-3"
      onClick={onClose}
      data-testid="lots-dialog"
    >
      <div
        className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 bg-gradient-to-r from-[#5B3B8C] to-[#7C4FB8] px-5 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            <h2 className="font-semibold">Saisie multi-lots</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="lots-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-3">
          {line && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5">
              <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium">Produit</p>
              <p className="text-sm font-semibold text-zinc-900">{line.product_name}</p>
              <p className="text-xs text-zinc-500">
                Stock théorique : <strong>{stockTh}</strong>
                {line.ean && <> · EAN <span className="font-mono">{line.ean}</span></>}
              </p>
            </div>
          )}

          <p className="text-xs text-zinc-600">
            Pour les produits ayant <strong>plusieurs dates de péremption</strong> en rayon, créez un lot par date.
            Le total physique et la péremption la plus proche seront calculés automatiquement.
          </p>

          <div className="space-y-2">
            {lots.map((lot, idx) => (
              <div
                key={idx}
                className="rounded-lg border border-zinc-200 bg-white p-2.5"
                data-testid={`lot-row-${idx}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-zinc-600">Lot {idx + 1}</span>
                  {lots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLot(idx)}
                      className="text-red-500 hover:text-red-700 p-1"
                      data-testid={`lot-delete-${idx}`}
                      aria-label="Supprimer ce lot"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-[10px] text-zinc-500 col-span-1">
                    Quantité
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={lot.qty}
                      onChange={(e) => updateLot(idx, 'qty', e.target.value)}
                      className="mt-0.5 w-full text-sm border border-zinc-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#5B3B8C]"
                      placeholder="0"
                      data-testid={`lot-qty-${idx}`}
                    />
                  </label>
                  <label className="text-[10px] text-zinc-500 col-span-1">
                    Péremption
                    <input
                      type="date"
                      value={lot.expiration_date}
                      onChange={(e) => updateLot(idx, 'expiration_date', e.target.value)}
                      className="mt-0.5 w-full text-sm border border-zinc-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#5B3B8C]"
                      data-testid={`lot-date-${idx}`}
                    />
                  </label>
                  <label className="text-[10px] text-zinc-500 col-span-2">
                    N° lot fournisseur (facultatif)
                    <input
                      type="text"
                      value={lot.lot_number}
                      onChange={(e) => updateLot(idx, 'lot_number', e.target.value)}
                      className="mt-0.5 w-full text-sm border border-zinc-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#5B3B8C]"
                      placeholder="ex: ABC123"
                      data-testid={`lot-number-${idx}`}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addLot}
            className="w-full text-sm border border-dashed border-purple-300 text-[#5B3B8C] rounded-lg py-2 hover:bg-purple-50 flex items-center justify-center gap-1"
            data-testid="lots-add"
          >
            <Plus className="w-4 h-4" /> Ajouter un lot
          </button>

          {/* Récap total */}
          <div className="rounded-md bg-purple-50 border border-purple-200 p-2.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">Total physique</span>
              <span className="text-2xl font-bold text-[#5B3B8C]" data-testid="lots-total">{total}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-zinc-600">Théorique</span>
              <span className="text-sm font-mono">{stockTh}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-zinc-600">Écart</span>
              <span className={`text-sm font-mono font-bold ${ecart === 0 ? 'text-emerald-700' : ecart > 0 ? 'text-blue-700' : 'text-red-700'}`}>
                {ecart > 0 ? '+' : ''}{ecart}
              </span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="shrink-0 sticky bottom-0 border-t border-zinc-200 bg-white px-4 py-3 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" data-testid="lots-cancel">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="flex-1 bg-[#5B3B8C] hover:bg-[#4A2F73] text-white disabled:opacity-50"
            data-testid="lots-save"
          >
            {saving ? 'Enregistrement…' : `Enregistrer ${total > 0 ? `(${total} unités)` : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LotsDialog;
